'use client';

import { useTheme } from '@emotion/react';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';

import { Grid } from '@hrworks/sui-core/Grid';
import { GridItem } from '@hrworks/sui-core/GridItem';
import { MaterialDesignIcon } from '@hrworks/sui-core/MaterialDesignIcon';
import { Section } from '@hrworks/sui-core/Section';

import { sanitizeSearchQuery } from '../../util/sanitizeSearchQuery';
import { useUrlSearchParams } from '../../util/useUrlSearchParams';
import { Button } from '../Button';
import { S } from './Search.styles';
import type { SearchProps } from './Search.types';

const filterKeys = ['searchQuery', 'location', 'discipline', 'level', 'type', 'time'];

export const Search = ({ searchOptions }: SearchProps) => {
  const t = useTranslations();
  const [params, setParams] = useUrlSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const [expanded, setExpanded] = useState(true);
  const searchQuery = params.get('searchQuery') || '';
  const theme = useTheme();

  const [searchInput, setSearchInput] = useState(searchQuery);

  // Sync local input when URL param is cleared (e.g. reset or X button)
  const [prevSearchQuery, setPrevSearchQuery] = useState(searchQuery);
  if (searchQuery !== prevSearchQuery) {
    setPrevSearchQuery(searchQuery);
    setSearchInput(searchQuery);
  }

  const updateParam = (key: string, value: string) => {
    setParams((p) => {
      if (value && value !== 'all') {
        p.set(key, value);
      } else {
        p.delete(key);
      }

      return p;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateParam('searchQuery', sanitizeSearchQuery(searchInput));
  };

  const onReset = () => {
    formRef.current?.reset();
    setSearchInput('');
    setParams((p) => {
      filterKeys.forEach((key) => p.delete(key));

      return p;
    });
  };

  const onResetSearchQuery = () => {
    setSearchInput('');
    setParams((p) => {
      p.delete('searchQuery');

      return p;
    });
  };

  return (
    <S.Container data-guide-id="search-container">
      <form onSubmit={handleSubmit} ref={formRef}>
        <Grid>
          <GridItem>
            <S.InputContainer>
              <S.TextField
                data-guide-id="search-input"
                name="searchQuery"
                value={searchInput}
                onValueChange={setSearchInput}
                placeholder={t('Main.search')}
                aria-label="Search"
                {...(searchInput.trim() && {
                  endSlot: ({ interactiveStyles }) => (
                    <Button
                      css={interactiveStyles}
                      variant="unstyled"
                      data-guide-id="search-clear-input"
                      onClick={onResetSearchQuery}
                    >
                      <MaterialDesignIcon name="close" />
                    </Button>
                  ),
                })}
              />
              <S.SubmitButton data-guide-id="search-submit-btn" type="submit">
                {t('Main.search')}
              </S.SubmitButton>
              <S.SubmitIconButton
                data-guide-id="search-submit-icon-btn"
                type="submit"
                variant="unstyled"
                aria-label={t('Main.search')}
              >
                <MaterialDesignIcon name="search" />
              </S.SubmitIconButton>
            </S.InputContainer>
          </GridItem>
          <GridItem>
            <Section
              id="advanced-search"
              title={t('Main.advanced-search')}
              uppercase
              collapsible
              expanded={expanded}
              onExpandedChange={() => setExpanded(!expanded)}
              slotProps={{
                title: { component: 'h2' },
                toggle: {
                  variant: 'unstyled',
                  'data-guide-id': 'search-advanced-toggle-btn',
                },
                toggleIcon: {
                  style: { color: theme.stellenportal?.fontStyle?.color },
                },
              }}
              data-guide-id="search-advanced-section"
            >
              <S.SectionContainer>
                <Grid size={{ xs: 'default', sm: 4 }}>
                  <GridItem>
                    <S.Select
                      data-guide-id="filter-location-select"
                      name="location"
                      labelPlacement="inside"
                      label={t('Job.place-of-work-type')}
                      options={searchOptions.placeOfWorkType}
                      onValueChange={(value) => updateParam('location', value)}
                      value={params.get('location') || 'all'}
                    />
                  </GridItem>
                  <GridItem>
                    <S.Select
                      data-guide-id="filter-discipline-select"
                      name="discipline"
                      labelPlacement="inside"
                      label={t('Job.scope-of-activities')}
                      options={searchOptions.scopeOfActivities}
                      onValueChange={(value) => updateParam('discipline', value)}
                      value={params.get('discipline') || 'all'}
                    />
                  </GridItem>
                  <GridItem>
                    <S.Select
                      data-guide-id="filter-level-select"
                      name="level"
                      labelPlacement="inside"
                      label={t('Job.career-level')}
                      options={searchOptions.careerLevel}
                      onValueChange={(value) => updateParam('level', value)}
                      value={params.get('level') || 'all'}
                    />
                  </GridItem>
                  <GridItem>
                    <S.Select
                      data-guide-id="filter-type-select"
                      name="employmentContractKey"
                      labelPlacement="inside"
                      label={t('Job.employmentContractType')}
                      options={searchOptions.employmentContractType}
                      onValueChange={(value) => updateParam('type', value)}
                      value={params.get('type') || 'all'}
                    />
                  </GridItem>
                  <GridItem>
                    <S.Select
                      data-guide-id="filter-time-select"
                      name="time"
                      labelPlacement="inside"
                      label={t('Job.working-time-model')}
                      options={searchOptions.workingTimeModel}
                      onValueChange={(value) => updateParam('time', value)}
                      value={params.get('time') || 'all'}
                    />
                  </GridItem>
                </Grid>
                <S.ResetButton data-guide-id="filter-reset-btn" variant="subtle" onClick={onReset}>
                  {t('Main.reset')}
                </S.ResetButton>
              </S.SectionContainer>
            </Section>
          </GridItem>
        </Grid>
      </form>
    </S.Container>
  );
};
